/**
 * InvestigationTypesPage - Pagina de gestionare a tipurilor de investigații
 * Design compact consistent cu restul aplicației
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  CircularProgress,
  Grid,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  LocalHospital as InvestigationIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import {
  InvestigationCategory,
  InvestigationCategoryCreate,
  InvestigationTypeListItem,
  InvestigationTypeCreate,
  InvestigationTypeUpdate,
  InvestigationTypesFilters,
} from '../types/investigationTypes';
import {
  getInvestigationCategories,
  createInvestigationCategory,
  updateInvestigationCategory,
  deleteInvestigationCategory,
  getInvestigationTypes,
  getInvestigationType,
  createInvestigationType,
  updateInvestigationType,
  deleteInvestigationType,
} from '../services/investigationTypesApi';

// Compact styles - consistent cu restul aplicației
const compactInputSx = {
  '& .MuiInputBase-root': { fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
};

const tableHeaderSx = {
  bgcolor: 'grey.900',
  '& th': {
    color: 'common.white',
    fontSize: '0.75rem',
    fontWeight: 600,
    py: 0.5,
    px: 1
  }
};

const tableCellSx = {
  fontSize: '0.8rem',
  py: 0.5,
  px: 1
};

const dialogTitleSx = {
  py: 1.5,
  px: 2,
  fontSize: '1rem',
  fontWeight: 600,
  borderBottom: '1px solid',
  borderColor: 'divider'
};

const dialogActionsSx = {
  px: 2,
  py: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider'
};

const tabsSx = {
  minHeight: 32,
  mb: 1.5,
  '& .MuiTab-root': {
    minHeight: 32,
    py: 0.5,
    px: 1.5,
    fontSize: '0.78rem',
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      mr: 0.5
    }
  }
};

const InvestigationTypesPage: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'types' | 'categories'>('types');
  
  // ============================================================
  // Categories State
  // ============================================================
  const [categories, setCategories] = useState<InvestigationCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<InvestigationCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState<InvestigationCategoryCreate>({
    code: '',
    name: '',
    description: '',
    display_order: 0,
  });
  
  // ============================================================
  // Investigation Types State
  // ============================================================
  const [investigationTypes, setInvestigationTypes] = useState<InvestigationTypeListItem[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState<InvestigationTypesFilters>({
    skip: 0,
    limit: 25,
    isActive: true,
  });
  const [searchInput, setSearchInput] = useState('');
  
  // Dialog
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<InvestigationTypeListItem | null>(null);
  const [typeFormData, setTypeFormData] = useState<InvestigationTypeCreate>({
    category_id: '',
    code: '',
    name: '',
    default_duration_minutes: 30,
    preparation_required: false,
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'category' | 'investigationType'; item: any } | null>(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================
  // Load Categories
  // ============================================================
  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await getInvestigationCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      setSnackbar({ open: true, message: 'Eroare la încărcarea categoriilor', severity: 'error' });
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ============================================================
  // Load Investigation Types
  // ============================================================
  const loadInvestigationTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const response = await getInvestigationTypes(filters);
      setInvestigationTypes(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading investigation types:', error);
      setSnackbar({ open: true, message: 'Eroare la încărcarea tipurilor', severity: 'error' });
    } finally {
      setTypesLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadInvestigationTypes();
  }, [loadInvestigationTypes]);

  // ============================================================
  // Category Handlers
  // ============================================================
  const handleOpenCategoryCreate = () => {
    setEditingCategory(null);
    setCategoryFormData({
      code: '',
      name: '',
      description: '',
      display_order: categories.length,
    });
    setCategoryDialogOpen(true);
  };

  const handleOpenCategoryEdit = (category: InvestigationCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      code: category.code,
      name: category.name,
      description: category.description || '',
      display_order: category.display_order,
    });
    setCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async () => {
    if (!categoryFormData.code || !categoryFormData.name) {
      setSnackbar({ open: true, message: 'Completați codul și numele', severity: 'error' });
      return;
    }

    setFormLoading(true);
    try {
      if (editingCategory) {
        await updateInvestigationCategory(editingCategory.id, categoryFormData);
        setSnackbar({ open: true, message: 'Categoria a fost actualizată', severity: 'success' });
      } else {
        await createInvestigationCategory(categoryFormData);
        setSnackbar({ open: true, message: 'Categoria a fost creată', severity: 'success' });
      }
      setCategoryDialogOpen(false);
      loadCategories();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Eroare la salvare';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // ============================================================
  // Investigation Type Handlers
  // ============================================================
  const handleSearch = useCallback(() => {
    setFilters(prev => ({ ...prev, search: searchInput, skip: 0 }));
  }, [searchInput]);

  const handleFilterChange = (key: keyof InvestigationTypesFilters, value: string | number | boolean | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value, skip: 0 }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, skip: newPage * (prev.limit || 25) }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, limit: parseInt(event.target.value, 10), skip: 0 }));
  };

  const handleOpenTypeCreate = () => {
    setEditingType(null);
    setTypeFormData({
      category_id: categories.length > 0 ? categories[0].id : '',
      code: '',
      name: '',
      default_duration_minutes: 30,
      preparation_required: false,
    });
    setTypeDialogOpen(true);
  };

  const handleOpenTypeEdit = async (type: InvestigationTypeListItem) => {
    setEditingType(type);
    setFormLoading(true);
    setTypeDialogOpen(true);
    
    try {
      const fullType = await getInvestigationType(type.id);
      setTypeFormData({
        category_id: fullType.category_id,
        code: fullType.code,
        name: fullType.name,
        short_name: fullType.short_name,
        description: fullType.description,
        default_duration_minutes: fullType.default_duration_minutes,
        preparation_required: fullType.preparation_required,
        preparation_instructions: fullType.preparation_instructions,
        base_price: fullType.base_price,
      });
    } catch (error) {
      console.error('Error loading type details:', error);
      setSnackbar({ open: true, message: 'Eroare la încărcarea detaliilor', severity: 'error' });
      setTypeDialogOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleTypeSubmit = async () => {
    if (!typeFormData.category_id || !typeFormData.code || !typeFormData.name) {
      setSnackbar({ open: true, message: 'Completați toate câmpurile obligatorii', severity: 'error' });
      return;
    }

    setFormLoading(true);
    try {
      if (editingType) {
        const updateData: InvestigationTypeUpdate = { ...typeFormData };
        await updateInvestigationType(editingType.id, updateData);
        setSnackbar({ open: true, message: 'Tipul de investigație a fost actualizat', severity: 'success' });
      } else {
        await createInvestigationType(typeFormData);
        setSnackbar({ open: true, message: 'Tipul de investigație a fost creat', severity: 'success' });
      }
      setTypeDialogOpen(false);
      loadInvestigationTypes();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Eroare la salvare';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // ============================================================
  // Delete Handlers
  // ============================================================
  const handleOpenDelete = (type: 'category' | 'investigationType', item: any) => {
    setItemToDelete({ type, item });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'category') {
        await deleteInvestigationCategory(itemToDelete.item.id);
        setSnackbar({ open: true, message: 'Categoria a fost dezactivată', severity: 'success' });
        loadCategories();
      } else {
        await deleteInvestigationType(itemToDelete.item.id);
        setSnackbar({ open: true, message: 'Tipul de investigație a fost dezactivat', severity: 'success' });
        loadInvestigationTypes();
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Eroare la ștergere';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // ============================================================
  // Render Categories Tab
  // ============================================================
  const renderCategoriesTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCategoryCreate}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        >
          Adaugă Categorie
        </Button>
      </Box>

      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, p: 1.5, pb: 0.5 }}>
            Lista Categorii ({categories.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeaderSx}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell align="center">Ordine</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoriesLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      Nu există categorii
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover sx={{ '& td': tableCellSx }}>
                    <TableCell>
                      <Chip
                        icon={<CategoryIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label={category.code}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {category.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
                        {category.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{category.display_order}</TableCell>
                    <TableCell>
                      <Chip
                        label={category.is_active ? 'Activ' : 'Inactiv'}
                        color={category.is_active ? 'success' : 'error'}
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editează">
                        <IconButton size="small" onClick={() => handleOpenCategoryEdit(category)}>
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dezactivează">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDelete('category', category)} 
                          color="error"
                          disabled={!category.is_active}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );

  // ============================================================
  // Render Investigation Types Tab
  // ============================================================
  const renderTypesTab = () => (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Caută după cod, nume..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={compactInputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={compactInputSx}>
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={filters.categoryId || ''}
                  onChange={(e) => handleFilterChange('categoryId', e.target.value || undefined)}
                  label="Categorie"
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                      {cat.code} - {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.isActive !== false}
                    onChange={(e) => handleFilterChange('isActive', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Doar active</Typography>}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                >
                  Caută
                </Button>
                <IconButton onClick={loadInvestigationTypes} size="small" title="Reîncarcă">
                  <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenTypeCreate}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                >
                  Adaugă Tip
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, p: 1.5, pb: 0.5 }}>
            Lista Tipuri Investigații ({total})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeaderSx}>
                <TableCell>Cod</TableCell>
                <TableCell>Denumire</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell align="center">Durată</TableCell>
                <TableCell align="center">Pregătire</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {typesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : investigationTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      Nu există tipuri de investigații
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                investigationTypes.map((type) => (
                  <TableRow key={type.id} hover sx={{ '& td': tableCellSx }}>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {type.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem' }}>{type.name}</Typography>
                      {type.short_name && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          ({type.short_name})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={type.category_code}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<ScheduleIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label={`${type.default_duration_minutes} min`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.68rem' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {type.preparation_required && (
                        <Tooltip title="Necesită pregătire">
                          <WarningIcon sx={{ fontSize: '1rem' }} color="warning" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={type.is_active ? 'Activ' : 'Inactiv'}
                        color={type.is_active ? 'success' : 'error'}
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editează">
                        <IconButton size="small" onClick={() => handleOpenTypeEdit(type)}>
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dezactivează">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDelete('investigationType', type)} 
                          color="error"
                          disabled={!type.is_active}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={Math.floor((filters.skip || 0) / (filters.limit || 25))}
            onPageChange={handleChangePage}
            rowsPerPage={filters.limit || 25}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Pe pagină:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} din ${count}`}
            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
          />
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box>
      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)}
        sx={tabsSx}
      >
        <Tab 
          icon={<InvestigationIcon />} 
          iconPosition="start" 
          label="Tipuri Investigații" 
          value="types" 
        />
        <Tab 
          icon={<CategoryIcon />} 
          iconPosition="start" 
          label="Categorii" 
          value="categories" 
        />
      </Tabs>

      {/* Content */}
      {activeTab === 'types' ? renderTypesTab() : renderCategoriesTab()}

      {/* Category Dialog - Compact */}
      <Dialog 
        open={categoryDialogOpen} 
        onClose={() => setCategoryDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {editingCategory ? 'Editează Categorie' : 'Adaugă Categorie Nouă'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Cod *"
                value={categoryFormData.code}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="CT, MR, RX"
                inputProps={{ maxLength: 20 }}
                sx={compactInputSx}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Denumire *"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                sx={compactInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Descriere"
                multiline
                rows={2}
                value={categoryFormData.description || ''}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                sx={compactInputSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Ordine afișare"
                type="number"
                value={categoryFormData.display_order}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, display_order: Number(e.target.value) }))}
                sx={compactInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setCategoryDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCategorySubmit} 
            disabled={formLoading}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {formLoading ? <CircularProgress size={16} /> : (editingCategory ? 'Salvează' : 'Adaugă')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Investigation Type Dialog - Compact */}
      <Dialog 
        open={typeDialogOpen} 
        onClose={() => setTypeDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {editingType ? 'Editează Tip Investigație' : 'Adaugă Tip Investigație Nou'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {formLoading && editingType ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required sx={compactInputSx}>
                  <InputLabel>Categorie</InputLabel>
                  <Select
                    value={typeFormData.category_id}
                    onChange={(e) => setTypeFormData(prev => ({ ...prev, category_id: e.target.value }))}
                    label="Categorie"
                  >
                    {categories.filter(c => c.is_active).map((cat) => (
                      <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                        {cat.code} - {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cod *"
                  value={typeFormData.code}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="CT-CRANIU-N"
                  inputProps={{ maxLength: 50 }}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Denumire completă *"
                  value={typeFormData.name}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="CT Craniu nativ"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Denumire scurtă"
                  value={typeFormData.short_name || ''}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, short_name: e.target.value }))}
                  placeholder="CT Craniu"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Durată (min)"
                  type="number"
                  value={typeFormData.default_duration_minutes}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, default_duration_minutes: Number(e.target.value) }))}
                  inputProps={{ min: 5, max: 480 }}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preț bază (RON)"
                  type="number"
                  value={typeFormData.base_price || ''}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, base_price: e.target.value ? Number(e.target.value) : null }))}
                  placeholder="Opțional"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descriere"
                  multiline
                  rows={2}
                  value={typeFormData.description || ''}
                  onChange={(e) => setTypeFormData(prev => ({ ...prev, description: e.target.value }))}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={typeFormData.preparation_required || false}
                      onChange={(e) => setTypeFormData(prev => ({ ...prev, preparation_required: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.8rem' }}>Necesită pregătire</Typography>}
                />
              </Grid>
              
              {typeFormData.preparation_required && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Instrucțiuni de pregătire"
                    multiline
                    rows={2}
                    value={typeFormData.preparation_instructions || ''}
                    onChange={(e) => setTypeFormData(prev => ({ ...prev, preparation_instructions: e.target.value }))}
                    placeholder="Ex: A jeun 6 ore, fără obiecte metalice..."
                    sx={compactInputSx}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setTypeDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button 
            variant="contained" 
            onClick={handleTypeSubmit} 
            disabled={formLoading}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {formLoading ? <CircularProgress size={16} /> : (editingType ? 'Salvează' : 'Adaugă')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Compact */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>Confirmă Dezactivarea</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>
            Sigur doriți să dezactivați{' '}
            {itemToDelete?.type === 'category' ? 'categoria' : 'tipul de investigație'}{' '}
            <strong>{itemToDelete?.item?.code || itemToDelete?.item?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmDelete}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Dezactivează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ fontSize: '0.78rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvestigationTypesPage;
